import { useEditor, EditorContent, type Editor, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  docToMarkdown,
  markdownToDoc,
  type PMDoc,
} from '../../lib/vault/tiptap/markdownDoc';
import { testIds } from '../../lib/testIds';
import './PreviewTipTapEditor.css';

// Phase D — sidecar overlay shapes. These mirror the Convex sidecar
// rows from `docxFidelity.getSidecarBundle` but only carry what the
// editor needs to *render* — author/date for tooltips, offsets +
// text for placement. Persistence shape lives in convex/schema.ts
// (docxComments, docxRevisions, docxFootnotes).
export interface DocxSidecarComment {
  docxCommentId: string;
  authorName?: string | null;
  date?: number | null;
  commentText: string;
  startOffset: number;
  endOffset: number;
}
export interface DocxSidecarRevision {
  docxRevisionId: string;
  type: 'insertion' | 'deletion' | 'move';
  authorName?: string | null;
  date?: number | null;
  text: string;
  startOffset: number;
  endOffset: number;
}
export interface DocxSidecarFootnote {
  docxFootnoteId: string;
  noteNumber: number;
  content: string;
  refOffset: number;
}

export interface PreviewTipTapEditorProps {
  value: string;
  onChange: (markdown: string) => void;
  testIdRoot?: string;
  /**
   * Optional Phase D sidecar overlays. When present, the editor paints
   * comments/revisions/footnotes as view-only Tiptap Decorations on
   * top of the doc — NOT real marks (those would require schema
   * changes and round-trip through markdownDoc.ts). Save-back
   * preserves sidecars unchanged because they live in Convex; only
   * the markdown body is `onChange`'d back to the caller.
   */
  sidecars?: {
    comments?: DocxSidecarComment[];
    revisions?: DocxSidecarRevision[];
    footnotes?: DocxSidecarFootnote[];
  };
  /**
   * Fired when a footnote marker is clicked. Caller renders the
   * note content in a drawer / popover.
   */
  onFootnoteClick?: (footnote: DocxSidecarFootnote) => void;
}

interface ToolbarButtonProps {
  editor: Editor;
  label: string;
  testId: string;
  onClick: () => void;
  isActive?: boolean;
}

function ToolbarButton({ editor, label, testId, onClick, isActive }: ToolbarButtonProps) {
  const disabled = !editor.isEditable;
  return (
    <button
      type="button"
      data-testid={testId}
      onMouseDown={(e) => {
        e.preventDefault();
        if (!disabled) onClick();
      }}
      disabled={disabled}
      className={`xp-tiptap-toolbar-button${isActive ? ' is-active' : ''}`}
      aria-pressed={isActive ? 'true' : 'false'}
    >
      {label}
    </button>
  );
}

// Phase D — sidecar decoration plugin. View-only overlay; no schema
// changes. Plain DOM markers + tooltips wrap the offset ranges. The
// editor stays semantically identical to the no-sidecar case so the
// markdownDoc round-trip is unaffected.
//
// Offset model: sidecars carry character offsets against the
// *markdown* body. Tiptap positions are 1-based ProseMirror positions
// (text + node tokens). To translate, we walk the doc and accumulate
// a markdown-char → PM-pos table, then look up start/end per sidecar.
// Approximation: assume plain text content (no fenced code / lists)
// for the first cut — round-trip fidelity is preserved either way
// because the decorations don't mutate the doc.
function buildOffsetMap(markdown: string, editor: Editor): Map<number, number> {
  // Brute-force scan for now: walk the markdown string + every PM
  // text node, advancing both pointers. Good enough for the matter
  // doc surface; revisit if performance bites on > 100 KB docs.
  void markdown;
  const out = new Map<number, number>();
  let mdPos = 0;
  editor.state.doc.descendants((node, pos) => {
    if (node.isText && typeof node.text === 'string') {
      for (let i = 0; i < node.text.length; i++) {
        out.set(mdPos, pos + i + 1);
        mdPos++;
      }
    }
    return true;
  });
  return out;
}

function sidecarDecorationPlugin(opts: {
  comments: DocxSidecarComment[];
  revisions: DocxSidecarRevision[];
  footnotes: DocxSidecarFootnote[];
  onFootnoteClick?: (fn: DocxSidecarFootnote) => void;
  value: string;
}): Plugin {
  return new Plugin({
    key: new PluginKey('docxSidecarDecorations'),
    props: {
      decorations(state) {
        const doc = state.doc;
        const decos: Decoration[] = [];

        // Build per-text-node a flat offset → PM-pos table.
        const offsetToPos = new Map<number, number>();
        let mdCursor = 0;
        doc.descendants((node, pos) => {
          if (node.isText && typeof node.text === 'string') {
            for (let i = 0; i < node.text.length; i++) {
              offsetToPos.set(mdCursor, pos + i);
              mdCursor++;
            }
          }
          return true;
        });
        const posFor = (offset: number): number | null => {
          const p = offsetToPos.get(offset);
          return typeof p === 'number' ? p : null;
        };

        // Comments → yellow background spans with title tooltip.
        for (const c of opts.comments) {
          const from = posFor(c.startOffset);
          const to = posFor(Math.max(c.startOffset, c.endOffset - 1));
          if (from === null || to === null) continue;
          decos.push(
            Decoration.inline(from, to + 1, {
              class: 'docx-sidecar-comment',
              title:
                (c.authorName ?? 'Unknown') +
                ': ' +
                c.commentText.slice(0, 280),
              'data-docx-comment-id': c.docxCommentId,
            }),
          );
        }

        // Revisions: insertions = green underline; deletions = red
        // strikethrough; moves = blue dashed.
        for (const r of opts.revisions) {
          const from = posFor(r.startOffset);
          const to = posFor(Math.max(r.startOffset, r.endOffset - 1));
          if (from === null || to === null) continue;
          const cls =
            r.type === 'insertion'
              ? 'docx-sidecar-revision-ins'
              : r.type === 'deletion'
              ? 'docx-sidecar-revision-del'
              : 'docx-sidecar-revision-move';
          decos.push(
            Decoration.inline(from, to + 1, {
              class: cls,
              title:
                r.type +
                ' by ' +
                (r.authorName ?? 'unknown') +
                (typeof r.date === 'number'
                  ? ' on ' + new Date(r.date).toISOString().slice(0, 10)
                  : ''),
              'data-docx-revision-id': r.docxRevisionId,
            }),
          );
        }

        // Footnotes: insert a superscript widget at the ref offset.
        for (const f of opts.footnotes) {
          const at = posFor(f.refOffset);
          if (at === null) continue;
          decos.push(
            Decoration.widget(
              at,
              () => {
                const sup = document.createElement('sup');
                sup.className = 'docx-sidecar-footnote-marker';
                sup.textContent = String(f.noteNumber);
                sup.title = f.content.slice(0, 280);
                sup.setAttribute('data-docx-footnote-id', f.docxFootnoteId);
                sup.style.cursor = opts.onFootnoteClick ? 'pointer' : 'default';
                if (opts.onFootnoteClick) {
                  sup.addEventListener('click', (e) => {
                    e.preventDefault();
                    opts.onFootnoteClick!(f);
                  });
                }
                return sup;
              },
              { side: 1 },
            ),
          );
        }
        return DecorationSet.create(doc, decos);
      },
    },
  });
}

const SidecarDecorations = Extension.create<{
  comments: DocxSidecarComment[];
  revisions: DocxSidecarRevision[];
  footnotes: DocxSidecarFootnote[];
  onFootnoteClick?: (fn: DocxSidecarFootnote) => void;
  value: string;
}>({
  name: 'docxSidecarDecorations',
  addOptions() {
    return {
      comments: [],
      revisions: [],
      footnotes: [],
      value: '',
    };
  },
  addProseMirrorPlugins() {
    return [sidecarDecorationPlugin(this.options)];
  },
});

export function PreviewTipTapEditor({
  value,
  onChange,
  testIdRoot,
  sidecars,
  onFootnoteClick,
}: PreviewTipTapEditorProps) {
  // Initial doc is computed once on mount; subsequent external `value`
  // changes flow through the useEffect below to avoid stomping in-flight
  // user input.
  const initialDocRef = useRef(markdownToDoc(value));
  const lastEmittedRef = useRef<string>(value);
  const [, forceRerender] = useState(0);

  // Memoize sidecar arrays so they're stable references for the
  // editor's plugin options — re-mounting the editor on every render
  // would be a perf disaster.
  const stableSidecars = useMemo(
    () => ({
      comments: sidecars?.comments ?? [],
      revisions: sidecars?.revisions ?? [],
      footnotes: sidecars?.footnotes ?? [],
    }),
    [sidecars?.comments, sidecars?.revisions, sidecars?.footnotes],
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        blockquote: false,
      }),
      SidecarDecorations.configure({
        comments: stableSidecars.comments,
        revisions: stableSidecars.revisions,
        footnotes: stableSidecars.footnotes,
        onFootnoteClick,
        value,
      }),
    ],
    content: initialDocRef.current,
    onUpdate({ editor }) {
      const md = docToMarkdown(editor.getJSON() as unknown as PMDoc);
      if (md !== lastEmittedRef.current) {
        lastEmittedRef.current = md;
        onChange(md);
      }
    },
    onSelectionUpdate() {
      forceRerender((n) => n + 1);
    },
  });
  // Reference buildOffsetMap once to keep it under TS6133 in case
  // we later wire it into a heading-aware mapper.
  void buildOffsetMap;

  useEffect(() => {
    if (!editor) return;
    const currentMd = docToMarkdown(editor.getJSON() as unknown as PMDoc);
    if (currentMd.trim() === value.trim()) return;
    const nextDoc = markdownToDoc(value);
    editor.commands.setContent(nextDoc, { emitUpdate: false });
    lastEmittedRef.current = value;
  }, [editor, value]);

  if (!editor) return null;

  const handleLink = () => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', previous ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div
      data-testid={testIdRoot ?? testIds.explorer.remoteDriveItemEditor}
      className="xp-tiptap-root"
    >
      <div className="xp-tiptap-toolbar" data-testid={`${testIdRoot ?? testIds.explorer.remoteDriveItemEditor}-toolbar`}>
        <ToolbarButton
          editor={editor}
          label="B"
          testId={`${testIdRoot ?? testIds.explorer.remoteDriveItemEditor}-bold`}
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
        />
        <ToolbarButton
          editor={editor}
          label="I"
          testId={`${testIdRoot ?? testIds.explorer.remoteDriveItemEditor}-italic`}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
        />
        <ToolbarButton
          editor={editor}
          label="Link"
          testId={`${testIdRoot ?? testIds.explorer.remoteDriveItemEditor}-link`}
          onClick={handleLink}
          isActive={editor.isActive('link')}
        />
      </div>
      <EditorContent editor={editor} className="xp-tiptap-content" />
    </div>
  );
}
